/**
 * ProvinceCitySelect — par de selects Estado (UF) → Cidade.
 *
 * A cidade fica bloqueada até haver um estado; trocar o estado limpa a cidade.
 * As listas vêm do serviço geo-location pelos hooks useProvinces/useCities
 * (ambos com cache em localStorage).
 *
 * Renderiza dois blocos irmãos (sem wrapper) para encaixar direto em grids do
 * formulário hospedeiro. Use `valueMode` para escolher o que sai no `city`:
 *  - 'name' (default) → nome da cidade (para payloads que gravam string)
 *  - 'id'             → id numérico como string (para payloads com city_id)
 *
 * Para pré-preencher a partir de um city_id, resolva antes com
 * `geoLocationApi.resolveCity(cityId)` e passe `state`/`city` já prontos.
 */
import { useEffect, useState } from 'react';
import useProvinces, { Province } from '../../hooks/useProvinces';
import useCities, { City } from '../../hooks/useCities';

interface Props {
    /** UF selecionada (initials, ex.: "SP") */
    state: string;
    /** Cidade selecionada — nome ou id, conforme `valueMode` */
    city: string;
    onChange: (next: { state: string; city: string }) => void;
    disabled?: boolean;
    valueMode?: 'name' | 'id';
    required?: boolean;
    stateLabel?: string;
    cityLabel?: string;
    /** classes do <select> e do <label>, para casar com o formulário hospedeiro */
    selectClassName?: string;
    labelClassName?: string;
}

const ProvinceCitySelect: React.FC<Props> = ({
    state, city, onChange, disabled, valueMode = 'name', required,
    stateLabel = 'Estado', cityLabel = 'Cidade', selectClassName, labelClassName,
}) => {
    const { fetchProvinces } = useProvinces();
    const { fetchCities } = useCities();
    const [provinces, setProvinces] = useState<Province[]>([]);
    const [cities, setCities] = useState<City[]>([]);
    const [loadingCities, setLoadingCities] = useState(false);

    useEffect(() => {
        let cancelled = false;
        fetchProvinces()
            .then((list: Province[]) => { if (!cancelled) setProvinces(list); })
            .catch(() => { /* sem lista de estados: selects ficam vazios */ });
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Carrega as cidades do estado atual. NÃO limpa a cidade — isso é só na troca
    // pelo usuário (handleState), senão um pré-preenchimento se perderia aqui.
    useEffect(() => {
        let cancelled = false;
        if (!state) { setCities([]); return; }
        setLoadingCities(true);
        fetchCities(state)
            .then((list: City[]) => { if (!cancelled) setCities(list); })
            .catch(() => { if (!cancelled) setCities([]); })
            .finally(() => { if (!cancelled) setLoadingCities(false); });
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [state]);

    const handleState = (nextState: string) => onChange({ state: nextState, city: '' });
    const handleCity = (nextCity: string) => onChange({ state, city: nextCity });

    const cityValue = (c: City) => (valueMode === 'id' ? String(c.id) : c.name);

    return (
        <>
            <div>
                <label className={labelClassName}>{stateLabel}{required ? ' *' : ''}</label>
                <select className={selectClassName} disabled={disabled} value={state}
                    onChange={e => handleState(e.target.value)}>
                    <option value="">Selecione o estado</option>
                    {provinces.map(p => (
                        <option key={p.initials} value={p.initials}>{p.initials} — {p.name}</option>
                    ))}
                </select>
            </div>
            <div>
                <label className={labelClassName}>{cityLabel}{required ? ' *' : ''}</label>
                <select className={selectClassName} disabled={disabled || !state || loadingCities}
                    value={city} onChange={e => handleCity(e.target.value)}>
                    <option value="">
                        {!state ? 'Selecione o estado primeiro' : loadingCities ? 'Carregando...' : 'Selecione a cidade'}
                    </option>
                    {/* cidade pré-preenchida que não está na lista carregada continua visível */}
                    {city && !cities.some(c => cityValue(c) === city) && (
                        <option value={city}>{city}</option>
                    )}
                    {cities.map(c => (
                        <option key={c.id} value={cityValue(c)}>{c.name}</option>
                    ))}
                </select>
            </div>
        </>
    );
};

export default ProvinceCitySelect;
